import { Category } from '@ampsec/platform-client';
import type { AnalyticsChartConfig } from './analyticsChartConfigUtils';

/**
 * Static chart configuration keyed by use case. Used as default and for i18n fallback.
 * Missing key for a category means no charts for that category.
 */
export const DEFAULT_ANALYTICS_CHART_CONFIGURATION: Record<string, AnalyticsChartConfig[]> = {
	default: [
		{
			id: 'finding-age-risk',
			title: 'Finding Age Risk',
			subtitle: 'Age of Open Findings by Month',
			datasets: [
				{ dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
				{ dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
			],
		},
		{
			id: 'finding-churn',
			title: 'Finding Churn',
			subtitle: 'Opened vs Closed Findings over time, by month',
			datasets: [
				{ dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
				{ dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
				{
					dataExpression: 'findingsClosed / findingsOpened',
					label: 'Attack Multiple (Closed/Open)',
					yAxisID: 'y1',
					color: '#FF2629',
				},
			],
		},
	],
	[Category.VULNERABILITY]: [
		{
			id: 'finding-age-risk',
			title: 'Vulnerability Risk',
			subtitle: 'Open Out-of-SLA vulnerabilities over time',
			datasets: [
				{ dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
				{ dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
			],
		},
		{
			id: 'finding-churn',
			title: 'Vulnerability Churn',
			subtitle: 'Opened vs Closed Out-of-SLA vulnerabilities over time',
			datasets: [
				{ dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
				{ dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
				{
					dataExpression: 'findingsClosed / findingsOpened',
					label: 'Attack Multiple (Closed/Open)',
					yAxisID: 'y1',
					color: '#FF2629',
				},
			],
		},
	],
  [Category.IDENTITY]: [
    {
      id: 'finding-age-risk',
      title: 'Identity Risk',
      subtitle: 'Open Identity Risk Findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
  ],
  [Category.TRAINING]: [
    {
      id: 'finding-age-risk',
      title: 'Overdue or Failed Training Interventions',
      subtitle: 'Open Training Mitigation Findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
    {
      id: 'finding-age-risk',
      title: 'Overdue or Failed Training Interventions Churn',
      subtitle: 'Opened vs Closed Overdue or Failed Training Interventions over time',
      datasets: [
        { dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
        { dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
      ],
    },
  ],
  [Category.MICROTRAINING]: [
    {
      id: 'finding-age-risk',
      title: 'Microtrainings Sent',
      subtitle: 'Microtrainings Sent vs Coaching Opportunities Over Time',
      datasets: [
        { dataExpression: 'healed', label: 'Microtrainings Sent (Healed Findings)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'findingsClosed', label: 'Microtraining Opportunities Closed (Closed Findings)', yAxisID: 'y1', color: '#FF2629', fill: true },
        { dataExpression: 'count', label: 'Coaching Opportunities (Open Findings)', color: '#FF1C8C' },
      ],
    },
  ],
  [Category.COMPLIANCE_TRAINING]: [
    {
      id: 'finding-age-risk',
      title: 'Compliance Training Completion',
      subtitle: 'Assigned vs Completed Compliance Training Over Time',
      datasets: [
        { dataExpression: 'findingsClosed / findingsOpened', label: 'Completion Rate (Completed/Assignments)', color: '#FF2629' },
        { dataExpression: 'findingsClosed', label: 'Completed (Closed Findings)', yAxisID: 'y', color: '#FF8C22aa', fill: true },
        { dataExpression: 'count', label: 'Training Assignments (Opened Findings)', yAxisID: 'y1', color: '#FF1C8C', fill: true },
      ],
    },
    {
      id: 'finding-age-risk',
      title: 'Time to Complete Compliance Training',
      subtitle: 'Time to Complete Compliance Training Over Time',
      datasets: [
        { dataExpression: 'averageDaysToHeal', label: 'Average Time to Complete (Days)', yAxisID: 'y1', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Training Assignments (Opened Findings)', yAxisID: 'y', color: '#FF1C8C', fill: true },
        { dataExpression: 'averageAge', label: 'Average Training Assignement Age (Days)', yAxisID: 'y1', color: '#FF8C22aa' },
      ],
    }
  ],
  [Category.EDR]: [
    {
      id: 'finding-age-risk',
      title: 'EDR Risk',
      subtitle: 'Open EDR Risk Findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
    {
      id: 'finding-age-risk',
      title: 'EDR Risk Churn',
      subtitle: 'Opened vs Closed EDR Risk Findings over time',
      datasets: [
        { dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
        { dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
        {
          dataExpression: 'findingsClosed / findingsOpened',
          label: 'Attack Multiple (Closed/Open)',
          yAxisID: 'y1',
          color: '#FF2629',
        },
      ],
    },
  ],
  [Category.ENDPOINT_MANAGEMENT]: [
    {
      id: 'finding-age-risk',
      title: 'Endpoint Management Risk',
      subtitle: 'Open Endpoint Management Findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
    {
      id: 'finding-age-risk',
      title: 'Endpoint Management Risk Churn',
      subtitle: 'Opened vs Closed Endpoint Management Findings over time',
      datasets: [
        { dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
        { dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
        {
          dataExpression: 'findingsClosed / findingsOpened',
          label: 'Attack Multiple (Closed/Open)',
          yAxisID: 'y1',
          color: '#FF2629',
        },
      ],
    },
  ],
  [Category.AI_SERVICE_RISK]: [
    {
      id: 'finding-age-risk',
      title: 'AI Service Risk',
      subtitle: 'Open AI Service Risk findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
    {
      id: 'ai-service-risk-churn',
      title: 'AI Service Risk Churn',
      subtitle: 'Opened vs Closed AI Service Risk findings over time',
      datasets: [
        { dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
        { dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
      ],
    },
  ],
  [Category.CUSTOM]: [
    {
      id: 'finding-age-risk',
      title: 'Custom Findings',
      subtitle: 'Open Custom Findings over time',
      datasets: [
        { dataExpression: 'averageAge', label: 'Average Finding Age (Days)', yAxisID: 'y', color: '#FF8C22aa' },
        { dataExpression: 'count', label: 'Open Findings', yAxisID: 'y1', color: '#FF2629', fill: true },
      ],
    },
    {
      id: 'finding-age-risk',
      title: 'Custom Findings Churn',
      subtitle: 'Opened vs Closed Custom Findings over time',
      datasets: [
        { dataExpression: 'findingsOpened', label: 'Arrival Rate (Opened Findings)', yAxisID: 'y', color: '#3b5edcaa' },
        { dataExpression: 'findingsClosed', label: 'Service Rate (Findings Closed)', color: '#ff912caa', fill: true },
      ],
    },
  ],
  'FLOW_SPEC': [
    {
      id: 'finding-age-risk',
      title: 'Finding Age Risk',
      subtitle: 'Age of Open Findings by Month',
      datasets: [
        {
          dataExpression: 'averageAge',
          label: 'Average Finding Age (Days)',
          yAxisID: 'y',
          color: '#FF8C22aa'
        },
        {
          dataExpression: 'count',
          label: 'Open Findings',
          yAxisID: 'y1',
          color: '#FF2629',
          fill: true,
          type: 'bar',
        }
      ]
    },
    {
      id: 'finding-churn',
      title: 'Finding Churn',
      subtitle: 'Opened vs Closed Findings over time, by month',
      datasets: [
        {
          dataExpression: 'findingsOpened',
          label: 'Arrival Rate (Opened Findings)',
          yAxisID: 'y',
          color: '#3b5edcaa'
        },
        {
          dataExpression: 'findingsClosed',
          label: 'Service Rate (Findings Closed)',
          color: '#ff912caa',
          fill: true
        },
        {
          dataExpression: 'findingsClosed / findingsOpened',
          label: 'Attack Multiple (Closed/Open)',
          yAxisID: 'y1',
          color: '#FF2629'
        }
      ]
    },
   {
      id: 'findings-aging',
      title: 'Finding Aging',
      subtitle: 'Finding arrival and service aging over time.',
      datasets: [
        {
          dataExpression: 'averageOpenedAge',
          label: 'Average Time to Open',
          yAxisID: 'y',
          color: '#3b5edcaa'
        },
        {
          dataExpression: 'averageDaysToHeal',
          label: 'Average Time to Close (MTTR)',
          color: '#ff912caa',
          fill: true
        },
        {
          dataExpression: 'averageDaysToHeal',
          label: 'Average Time to Close (MTTR) Goal',
          yAxisID: 'y1',
          color: '#FF2629'
        }
      ]
    },
   {
      id: 'finding-engagement',
      title: 'Finding Engagement',
      subtitle: 'Findings engaged and healed over time.',
      datasets: [
        {
          dataExpression: 'engaged',
          label: 'Engaged',
          yAxisID: 'y',
          color: '#3b5edcaa'
        },
        {
          dataExpression: 'healed',
          label: 'Healed',
          color: '#ff912caa',
          fill: true
        },
        {
          dataExpression: 'averageDaysToHeal',
          label: 'Average Time to Heal',
          yAxisID: 'y1',
          color: '#FF2629'
        }
     ]
    }
  ],
};
